import { getLiveScoresForTournament, LIVE_BOLT_SCORE_EVENT } from 'src/services/liveBoltScores';
import type { LiveBoltScoreEventDetail } from 'src/services/liveBoltScores';
import { removeAllChildNodes } from 'src/services/dom/transformers';
import type { PublicLivePayload, PublicLiveSide } from 'src/services/publicLiveTypes';
import { getScheduledMatchUps } from 'src/services/api/tournamentsApi';
import { dropDownButton } from 'src/components/buttons/dropDownButton';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { context } from 'src/common/context';
import dayjs from 'dayjs';

// ─── Socket.IO live card helpers ──────────────────────────────────────────────

function setsDisplay(side: PublicLiveSide): string {
  return (side.setScores ?? []).join(' ');
}

function scoreCell(side: PublicLiveSide): string {
  const sets = setsDisplay(side);
  const game = side.gameScore != null ? `<span class="ch-game">${side.gameScore}</span>` : '';
  const dot = side.isServing ? '<span class="ch-serve-dot" title="Serving"></span>' : '';
  return `<span class="ch-sets">${sets}</span>${game}${dot}`;
}

function renderSocketLiveCard(payload: PublicLivePayload): HTMLElement {
  const card = document.createElement('div');
  card.className = 'ch-live-card';
  card.dataset.matchupId = payload.matchUpId;
  card.innerHTML = `
    <div class="ch-live-header">
      <span class="ch-live-badge">LIVE</span>
    </div>
    <div class="ch-live-row">
      <span class="ch-player">${payload.side1.playerName || payload.side1.teamName || 'TBD'}</span>
      <span class="ch-score">${scoreCell(payload.side1)}</span>
    </div>
    <div class="ch-live-row">
      <span class="ch-player">${payload.side2.playerName || payload.side2.teamName || 'TBD'}</span>
      <span class="ch-score">${scoreCell(payload.side2)}</span>
    </div>
  `;
  return card;
}

function patchSocketLiveCard(container: HTMLElement, payload: PublicLivePayload): void {
  const card = container.querySelector<HTMLElement>(`[data-matchup-id="${payload.matchUpId}"]`);
  if (!card) { container.appendChild(renderSocketLiveCard(payload)); return; }
  if (payload.status === 'completed') { card.remove(); return; }
  const rows = card.querySelectorAll<HTMLElement>('.ch-live-row');
  if (rows[0]) rows[0].querySelector('.ch-score')!.innerHTML = scoreCell(payload.side1);
  if (rows[1]) rows[1].querySelector('.ch-score')!.innerHTML = scoreCell(payload.side2);
}

// ─── REST live card (IN_PROGRESS matchUp) ────────────────────────────────────

function renderRestLiveCard(matchUp: any): HTMLElement {
  const card = document.createElement('div');
  card.className = 'ch-live-card';
  card.dataset.matchupId = matchUp.matchUpId;

  const name1 = matchUp.sides?.[0]?.participant?.participantName ?? 'TBD';
  const name2 = matchUp.sides?.[1]?.participant?.participantName ?? 'TBD';
  const sets = matchUp.score?.sets ?? [];
  const lastSet = sets[sets.length - 1];
  const g1 = lastSet?.side1Score ?? '';
  const g2 = lastSet?.side2Score ?? '';
  const p1 = lastSet?.side1PointScore ?? '';
  const p2 = lastSet?.side2PointScore ?? '';
  const prevSets1 = sets.slice(0, -1).map((s: any) => s.side1Score).join(' ');
  const prevSets2 = sets.slice(0, -1).map((s: any) => s.side2Score).join(' ');
  const court = matchUp.schedule?.courtName ?? '';
  const round = matchUp.roundName ?? '';

  card.innerHTML = `
    <div class="ch-live-header">
      <span class="ch-live-badge">LIVE</span>
      ${court ? `<span class="ch-live-court">${court}</span>` : ''}
      ${round ? `<span class="ch-live-round">${round}</span>` : ''}
    </div>
    <div class="ch-live-row">
      <span class="ch-player">${name1}</span>
      <span class="ch-score-block">
        ${prevSets1 ? `<span class="ch-prev-sets">${prevSets1}</span>` : ''}
        <span class="ch-game">${g1}</span>
        ${p1 ? `<span class="ch-point">${p1}</span>` : ''}
      </span>
    </div>
    <div class="ch-live-row">
      <span class="ch-player">${name2}</span>
      <span class="ch-score-block">
        ${prevSets2 ? `<span class="ch-prev-sets">${prevSets2}</span>` : ''}
        <span class="ch-game">${g2}</span>
        ${p2 ? `<span class="ch-point">${p2}</span>` : ''}
      </span>
    </div>
  `;
  return card;
}

// ─── participant name ─────────────────────────────────────────────────────────

function participantDisplayName(participant: any): string {
  if (!participant) return 'TBD';
  if (participant.participantName) return participant.participantName;
  const parts: string[] = (participant.individualParticipants ?? []).map((p: any) => {
    const family = p?.person?.standardFamilyName ?? '';
    const given = p?.person?.standardGivenName?.[0] ?? '';
    return given ? `${given}. ${family}` : family;
  });
  return parts.filter(Boolean).join(' / ') || 'TBD';
}

// ─── process matchUps by date ─────────────────────────────────────────────────

function processMatchUps(data: any, selectedDate: string): { live: any[]; upcoming: any[]; results: any[] } {
  const mappedParticipants: Record<string, any> = data?.mappedParticipants ?? {};
  const live: any[] = [];
  const upcoming: any[] = [];
  const results: any[] = [];

  const allMatchUps = [
    ...(data?.dateMatchUps ?? []),
    ...(data?.completedMatchUps ?? []),
  ];

  for (const matchUp of allMatchUps) {
    const matchDate = matchUp.schedule?.scheduledDate ?? '';
    if (selectedDate && matchDate && matchDate !== selectedDate) continue;

    for (const side of matchUp.sides ?? []) {
      if (side.participantId) {
        side.participant = mappedParticipants[side.participantId];
        if (side.participant?.individualParticipantIds) {
          side.participant.individualParticipants = side.participant.individualParticipantIds.map(
            (id: string) => mappedParticipants[id],
          );
        }
      }
    }

    const row = {
      matchUpId: matchUp.matchUpId,
      scheduledTime: matchUp.schedule?.scheduledTime ?? '',
      courtName: matchUp.schedule?.courtName ?? '',
      side1Name: participantDisplayName(matchUp.sides?.[0]?.participant),
      side2Name: participantDisplayName(matchUp.sides?.[1]?.participant),
      score: matchUp.score?.scoreStringSide1 ?? '',
      roundName: matchUp.roundName ?? '',
    };

    if (matchUp.matchUpStatus === 'IN_PROGRESS') {
      live.push(matchUp);
    } else if (matchUp.matchUpStatus === 'COMPLETED') {
      results.push(row);
    } else {
      upcoming.push(row);
    }
  }

  upcoming.sort((a, b) => (a.scheduledTime || 'ZZZ').localeCompare(b.scheduledTime || 'ZZZ'));
  return { live, upcoming, results };
}

// ─── extract all scheduled dates ─────────────────────────────────────────────

function extractDates(data: any): string[] {
  const dates = new Set<string>();
  for (const m of [...(data?.dateMatchUps ?? []), ...(data?.completedMatchUps ?? [])]) {
    if (m.schedule?.scheduledDate) dates.add(m.schedule.scheduledDate);
  }
  return Array.from(dates).sort();
}

// ─── tabulator columns ────────────────────────────────────────────────────────

function timeFormatter(cell: any) {
  const t = cell.getValue();
  if (!t) return '–';
  if (/^\d{2}:\d{2}$/.test(t)) return t;
  try { return new Date(t).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }); }
  catch { return t; }
}

function getUpcomingColumns() {
  return [
    { title: 'Time', field: 'scheduledTime', width: 70, formatter: timeFormatter },
    { title: 'Court', field: 'courtName', width: 90 },
    { title: 'Player 1', field: 'side1Name', widthGrow: 2 },
    { title: 'Player 2', field: 'side2Name', widthGrow: 2 },
    { title: 'Round', field: 'roundName', widthGrow: 1 },
  ];
}

function getResultsColumns() {
  return [
    { title: 'Time', field: 'scheduledTime', width: 70, formatter: timeFormatter },
    { title: 'Court', field: 'courtName', width: 90 },
    { title: 'Player 1', field: 'side1Name', widthGrow: 2 },
    { title: 'Player 2', field: 'side2Name', widthGrow: 2 },
    { title: 'Score', field: 'score', width: 120 },
    { title: 'Round', field: 'roundName', widthGrow: 1 },
  ];
}

function emptyNote(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'ch-matches-empty';
  p.textContent = text;
  return p;
}

// ─── sub-tabs ────────────────────────────────────────────────────────────────

function renderSubTabs(container: HTMLElement, tabs: { label: string; id: string }[], activeId: string) {
  const bar = document.createElement('div');
  bar.className = 'ch-sub-tabs';
  tabs.forEach(({ label, id }) => {
    const btn = document.createElement('button');
    btn.className = 'ch-sub-tab' + (id === activeId ? ' ch-sub-tab--active' : '');
    btn.textContent = label;
    btn.onclick = () => {
      container.querySelectorAll<HTMLElement>('.ch-sub-tab').forEach(b => b.classList.remove('ch-sub-tab--active'));
      btn.classList.add('ch-sub-tab--active');
      container.querySelectorAll<HTMLElement>('.ch-sub-panel').forEach(p => { p.style.display = 'none'; });
      const panel = document.getElementById(id);
      if (panel) panel.style.display = 'block';
    };
    bar.appendChild(btn);
  });
  container.appendChild(bar);
}

// ─── render panels with data ──────────────────────────────────────────────────

function renderPanels(
  data: any,
  selectedDate: string,
  liveGrid: HTMLElement,
  liveEmpty: HTMLElement,
  upcomingPanel: HTMLElement,
  resultsPanel: HTMLElement,
) {
  const { live, upcoming, results } = processMatchUps(data, selectedDate);

  // Live panel
  removeAllChildNodes(liveGrid);
  if (live.length > 0) {
    live.forEach((m) => liveGrid.appendChild(renderRestLiveCard(m)));
    liveEmpty.style.display = 'none';
    liveGrid.style.display = 'grid';
  } else {
    liveEmpty.style.display = 'block';
    liveGrid.style.display = 'none';
  }

  // Upcoming panel – clear and re-render
  removeAllChildNodes(upcomingPanel);
  const upcomingTableEl = document.createElement('div');
  upcomingTableEl.id = 'ch-upcoming-table';
  upcomingPanel.appendChild(upcomingTableEl);
  if (upcoming.length === 0) {
    upcomingPanel.appendChild(emptyNote('No upcoming matches scheduled.'));
  } else {
    new Tabulator('#ch-upcoming-table', {
      data: upcoming, columns: getUpcomingColumns(), layout: 'fitColumns', height: 'auto', reactiveData: false,
    });
  }

  // Results panel – clear and re-render
  removeAllChildNodes(resultsPanel);
  const resultsTableEl = document.createElement('div');
  resultsTableEl.id = 'ch-results-table';
  resultsPanel.appendChild(resultsTableEl);
  if (results.length === 0) {
    resultsPanel.appendChild(emptyNote('No completed matches yet.'));
  } else {
    new Tabulator('#ch-results-table', {
      data: results, columns: getResultsColumns(), layout: 'fitColumns', height: 'auto', reactiveData: false,
    });
  }
}

// ─── main export ─────────────────────────────────────────────────────────────

let _liveListener: ((e: Event) => void) | undefined;

export function createMatchesTable(): void {
  const tournamentId: string = context.tournamentId;
  if (!tournamentId) return;
  const container = document.getElementById('tournament_matches');
  if (!container) return;

  removeAllChildNodes(container);
  if (_liveListener) { window.removeEventListener(LIVE_BOLT_SCORE_EVENT, _liveListener); _liveListener = undefined; }

  // ── header row (date selector placeholder) ───────────────────────────────
  const headerRow = document.createElement('div');
  headerRow.className = 'ch-matches-header-row';
  headerRow.id = 'ch-matches-header';
  container.appendChild(headerRow);

  // ── sub-tabs ──────────────────────────────────────────────────────────────
  renderSubTabs(container, [
    { label: 'Live now', id: 'ch-panel-live' },
    { label: 'Upcoming', id: 'ch-panel-upcoming' },
    { label: 'Results', id: 'ch-panel-results' },
  ], 'ch-panel-live');

  // ── Live panel ────────────────────────────────────────────────────────────
  const livePanel = document.createElement('div');
  livePanel.className = 'ch-sub-panel';
  livePanel.id = 'ch-panel-live';
  livePanel.style.display = 'block';
  const liveGrid = document.createElement('div');
  liveGrid.className = 'ch-live-grid';
  liveGrid.id = 'ch-live-grid';
  livePanel.appendChild(liveGrid);
  const liveEmpty = document.createElement('p');
  liveEmpty.className = 'ch-matches-empty';
  liveEmpty.id = 'ch-live-empty';
  liveEmpty.textContent = 'No matches in progress right now.';
  livePanel.appendChild(liveEmpty);
  container.appendChild(livePanel);

  // ── Upcoming panel ────────────────────────────────────────────────────────
  const upcomingPanel = document.createElement('div');
  upcomingPanel.className = 'ch-sub-panel';
  upcomingPanel.id = 'ch-panel-upcoming';
  upcomingPanel.style.display = 'none';
  container.appendChild(upcomingPanel);

  // ── Results panel ─────────────────────────────────────────────────────────
  const resultsPanel = document.createElement('div');
  resultsPanel.className = 'ch-sub-panel';
  resultsPanel.id = 'ch-panel-results';
  resultsPanel.style.display = 'none';
  container.appendChild(resultsPanel);

  // ── Socket.IO ─────────────────────────────────────────────────────────────
  const existing = getLiveScoresForTournament(tournamentId).filter((p) => p.status === 'in_progress');
  existing.forEach((p) => liveGrid.appendChild(renderSocketLiveCard(p)));
  _liveListener = (e: Event) => {
    const { payload } = (e as CustomEvent<LiveBoltScoreEventDetail>).detail;
    if (payload.tournamentId !== tournamentId) return;
    patchSocketLiveCard(liveGrid, payload);
    const hasCards = liveGrid.children.length > 0;
    liveEmpty.style.display = hasCards ? 'none' : 'block';
    liveGrid.style.display = hasCards ? 'grid' : 'none';
  };
  window.addEventListener(LIVE_BOLT_SCORE_EVENT, _liveListener);

  // ── REST fetch ────────────────────────────────────────────────────────────
  getScheduledMatchUps({ tournamentId }).then((result) => {
    const data = result?.data;
    const dates = extractDates(data);
    const today = new Date().toISOString().slice(0, 10);
    let selectedDate = dates.includes(today) ? today : (dates[0] ?? today);

    // Date selector dropdown (same pattern as createScheduleTable)
    if (dates.length > 1) {
      const dateOptions = dates.map((d) => ({
        onClick: () => {
          selectedDate = d;
          renderPanels(data, selectedDate, liveGrid, liveEmpty, upcomingPanel, resultsPanel);
        },
        label: dayjs(d).format('dddd MMM D'),
        value: d,
        close: true,
      }));
      const dateSelector = {
        label: dayjs(selectedDate).format('dddd MMM D'),
        options: dateOptions,
        id: 'matchesDateSelector',
        modifyLabel: true,
        selection: true,
      };
      const header = document.getElementById('ch-matches-header');
      if (header) header.appendChild(dropDownButton({ button: dateSelector }));
    }

    renderPanels(data, selectedDate, liveGrid, liveEmpty, upcomingPanel, resultsPanel);
  });
}

export function refreshMatchesLive(): void {
  const tournamentId: string = context.tournamentId;
  if (!tournamentId) return;
  const liveGrid = document.getElementById('ch-live-grid');
  if (!liveGrid) return;
  removeAllChildNodes(liveGrid);
  const live = getLiveScoresForTournament(tournamentId).filter((p) => p.status === 'in_progress');
  live.forEach((p) => liveGrid.appendChild(renderSocketLiveCard(p)));
  const liveEmpty = document.getElementById('ch-live-empty');
  if (liveEmpty) {
    liveEmpty.style.display = live.length > 0 ? 'none' : 'block';
    liveGrid.style.display = live.length > 0 ? 'grid' : 'none';
  }
}
